import React, { useState, useEffect } from 'react';
import CheckBox from '../CheckBox';
import FileUploader from '../FileUploader';
import { FileMetadataType, FileTableEntry, FileType } from '../../../store/Models/Documents/DocumentModels';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { ToxnotServiceClient } from '../../../store/ToxnotServiceClient';
import TextInput from '../TextInput';
import { DropdownMenu } from "../DropdownMenu";
import { TableColumn, TableRow, ToxnotReactTable } from '../ToxnotReactTable';
import DateInput from '../DateInput';
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../ToxnotReactModal';
import { TopActionButtons } from '../TopActionsAndScrollableContentContainer';

interface CmrtDetailsProps {
    placeholder?: string;
    value?: string;
    additionalClass?: string;
    disabled?: boolean;
    onValueChange: (value: string, valueComment?: string, files?: FileTableEntry[], fileIds?: string[]) => void;
    idPrefix?: string;
    fileList?: FileTableEntry[] | null | undefined;
    localizer: IStringLocalizer;
    inventoryId?: string;
}
interface ConflictMineralV6XmlTemplate {
    ConflictMinerals: ConflictMinerals;
}
interface ConflictMinerals {
    IntentionallyAddedOrUsed: ConflictMineralsAnswer;
    RemainedInProduct: ConflictMineralsAnswer;
    DrcOrigin: ConflictMineralsAnswer;
    ScrapOrigin: ConflictMineralsAnswer;
    PercentageSuppliersProvidedResponse: ConflictMineralsAnswer;
    KnownSmelters: ConflictMineralsAnswer;
    CfsValid: ConflictMineralsAnswer;
    Smelter3TGConflictAndHighRiskArea: ConflictMineralsAnswer;
    CompanyWideQuestions: ConflictMineralCompanyQuestions;
    CompanyInfo: CompanyInfo;
    DeclarationScope?: string;
    DescriptionOfScope?: string;
    ProductList: ProductList;
    SmelterList: SmelterList;
}
interface ConflictMineralsAnswer {
    Ta: string;
    Sn: string;
    Au: string;
    W: string;
    TaComment: string;
    SnComment: string;
    AuComment: string;
    WComment: string;
}
interface ConflictMineralCompanyQuestions {
    DrcConflictFreePolicy: string;
    DrcConflictFreePolicyComment: string;
    IsDrcConflictFreePolicyPublic: string;
    IsDrcConflictFreePolicyPublicComment: string;
    SuppliersMustUseCfsSmelters: string;
    SuppliersMustUseCfsSmeltersComment: string;
    DueDiligenceForCfs: string;
    DueDiligenceForCfsComment: string;
    ConductConflictMineralsSurvey: string;
    ConductConflictMineralsSurveyComment: string;
    VerificationIncludesCorrectiveActions: string;
    VerificationIncludesCorrectiveActionsComment: string;
    VerifySuppliersDueDiligence: string;
    VerifySuppliersDueDiligenceComment: string;
    SubjectToSecCmDisclosureRule: string;
    SubjectToSecCmDisclosureRuleComment: string;
}
interface CompanyInfo {
    CompanyName?: string;
    CompanyUniqueId?: string;
    DescriptionOfScope?: string;
    CompanyAddress?: string;
    ContactName?: string;
    ContactEmail?: string;
    ContactPhone?: {
        CountryCode?: string;
        Number?: string;
        AreaCode?: string;
    };
    CompanyUniqueIdAuthority?: string;
    DeclarationScope?: string;
    AuthorizerName?: string;
    AuthorizerTitle?: string;
    AuthorizerEmail?: string;
    AuthorizerPhone?: {
        CountryCode?: string;
        Number?: string;
        AreaCode?: string
    };
    EffectiveDate?: string;
}
interface ProductList {
    Product: Product[]
}
interface Product {
    ProductNumber: string;
    ProductName?: string;
    Comments?: string;
}
interface SmelterList {
    Smelter: Smelter[]
}
interface Smelter {
    City?: string;
    Comments?: string;
    ContactEmail?: string;
    ContactName?: string;
    Country?: string;
    DoesAllFeedstockComeFromScrapOrRecycled?: string;
    Id: string;
    IdNumber?: string;
    IdSource?: string;
    Metal?: string;
    MetalSource?: string;
    MineCountry?: string;
    MineName?: string;
    NextSteps?: string;
    ReferenceName?: string;
    State?: string;
    StreetAddress?: string;
}

interface CountryData {
    [code: string]: string;
};

enum Question {
    CfsValid = "CfsValid",
    DrcOrigin = "DrcOrigin",
    IntentionallyAddedOrUsed = "IntentionallyAddedOrUsed",
    KnownSmelters = "KnownSmelters",
    PercentageSuppliersProvidedResponse = "PercentageSuppliersProvidedResponse",
    RemainedInProduct = "RemainedInProduct",
    ScrapOrigin = "ScrapOrigin",
    Smelter3TGConflictAndHighRiskArea = "Smelter3TGConflictAndHighRiskArea"
}
enum Metal {
    Ta = "Ta",
    Sn = "Sn",
    Au = "Au",
    W = "W"
}
enum CompanyQuestion {
    DrcConflictFreePolicy = "DrcConflictFreePolicy",
    IsDrcConflictFreePolicyPublic = "IsDrcConflictFreePolicyPublic",
    SuppliersMustUseCfsSmelters = "SuppliersMustUseCfsSmelters",
    DueDiligenceForCfs = "DueDiligenceForCfs",
    ConductConflictMineralsSurvey = "ConductConflictMineralsSurvey",
    VerifySuppliersDueDiligence = "VerifySuppliersDueDiligence",
    VerificationIncludesCorrectiveActions = "VerificationIncludesCorrectiveActions",
    SubjectToSecCmDisclosureRule = "SubjectToSecCmDisclosureRule"
}
enum CompanyAnswer {
    Answer = "Answer"
}
enum ItemType {
    Product = "Product",
    Smelter = "Smelter",
}
type MetalKey = keyof typeof Metal;
type CompanyAnswerKey = keyof typeof CompanyQuestion;

const CmrtDetails: React.FC<CmrtDetailsProps> = ({
    placeholder,
    value,
    additionalClass,
    disabled,
    onValueChange,
    idPrefix,
    fileList,
    localizer,
    inventoryId,
}) => {
    const toxnotServiceClient = new ToxnotServiceClient();
    // TODO TREX-2861 - emptyCmrtJson is incorrect for Smelters and Products.
    // There is a bug where SmelterList and Product list are objects, with Smelter and Product as arrays.
    // This is backwards.
    // Due to the stackup of downstream code relying on this incorrect structure,
    // this can't be changed without a comprehensive plan to handle backwards compatibility / migrations
    // and remove all of the code relying on this incorrect structure.
    // Please see the ticket for other details. 
    const emptyCmrtJson: string = '{"ConflictMineralV6XmlTemplate":{"ConflictMinerals":{"CfsValid":{"Au":"","AuComment":"","Sn":"","SnComment":"","Ta":"","TaComment":"","W":"","WComment":""},"CompanyInfo":{"AuthorizerEmail":"","AuthorizerName":"","AuthorizerPhone":{"AreaCode":"","CountryCode":"","Number":"","Original":""},"AuthorizerTitle":"","CompanyAddress":"","CompanyName":"","CompanyUniqueId":"","CompanyUniqueIdAuthority":"","ContactEmail":"","ContactName":"","ContactPhone":{"AreaCode":"","CountryCode":"","Number":"","Original":""},"DeclarationScope":"","DescriptionOfScope":"","EffectiveDate":""},"CompanyWideQuestions":{"ConductConflictMineralsSurvey":"","ConductConflictMineralsSurveyComment":"","DrcConflictFreePolicy":"","DrcConflictFreePolicyComment":"","DueDiligenceForCfs":"","DueDiligenceForCfsComment":"","IsDrcConflictFreePolicyPublic":"","IsDrcConflictFreePolicyPublicComment":"","SubjectToSecCmDisclosureRule":"","SubjectToSecCmDisclosureRuleComment":"","SuppliersMustUseCfsSmelters":"","SuppliersMustUseCfsSmeltersComment":"","VerificationIncludesCorrectiveActions":"","VerificationIncludesCorrectiveActionsComment":"","VerifySuppliersDueDiligence":"","VerifySuppliersDueDiligenceComment":""},"DeclarationScope":"","DescriptionOfScope":"","DrcOrigin":{"Au":"","AuComment":"","Sn":"","SnComment":"","Ta":"","TaComment":"","W":"","WComment":""},"IntentionallyAddedOrUsed":{"Au":"","AuComment":"","Sn":"","SnComment":"","Ta":"","TaComment":"","W":"","WComment":""},"KnownSmelters":{"Au":"","AuComment":"","Sn":"","SnComment":"","Ta":"","TaComment":"","W":"","WComment":""},"PercentageSuppliersProvidedResponse":{"Au":"","AuComment":"","Sn":"","SnComment":"","Ta":"","TaComment":"","W":"","WComment":""},"ProductList":{"Product":[]},"RemainedInProduct":{"Au":"","AuComment":"","Sn":"","SnComment":"","Ta":"","TaComment":"","W":"","WComment":""},"ScrapOrigin":{"Au":"","AuComment":"","Sn":"","SnComment":"","Ta":"","TaComment":"","W":"","WComment":""},"Smelter3TGConflictAndHighRiskArea":{"Au":"","AuComment":"","Sn":"","SnComment":"","Ta":"","TaComment":"","W":"","WComment":""},"SmelterList":{"Smelter":[]}}}}';
    const initialCmrtDetailsValue = idPrefix === "cm_topic_xml_json" ? emptyCmrtJson : "";
    const emptySmelter = JSON.parse('{"Metal":"","ReferenceName":"","Country":"","Id":"","IdNumber":"","IdSource":"","MetalSource":"","StreetAddress":"","City":"","State":"","ContactName":"","ContactEmail":"","NextSteps":"","MineName":"","MineCountry":"","DoesAllFeedstockComeFromScrapOrRecycled":"","Comments":""}');
    const emptyProduct = JSON.parse('{"ProductNumber":"","ProductName":"","Comments":""}');

    const complianceQuestionnaire = {
        "IntentionallyAddedOrUsed": { id: "cm_3tg_added_or_used", question: "Is any 3TG intentionally added or used in the product(s) or in the production processes?" },
        "RemainedInProduct": { id: "cm_3tg_remains", question: "Does any 3TG remain in the product?" },
        "DrcOrigin": { id: "cm_3tg_from_covered_countries", question: "Do any of the smelters in your supply chain source the 3TG from the covered countries?" },
        "ScrapOrigin": { id: "cm_3tg_all_recycled", question: "Does 100% of the 3TG (necessary to the functionality or production of your products) originate from recycled or scrap sources?" },
        "PercentageSuppliersProvidedResponse": { id: "cm_suppliers_response_rate", question: "What percentage of relevant suppliers have posted a response to your supply chain survey?" },
        "KnownSmelters": { id: "cm_all_smelters_identified", question: "Have you identified all of the smelters supplying the 3TG to your supply chain?" },
        "CfsValid": { id: "cm_smelter_info_received", question: "Has all applicable smelter information received by your company been reported in this declaration?" },
        "Smelter3TGConflictAndHighRiskArea": { id: "cm_conflict_affected_high_risk_areas", question: "Do any of the smelters in your supply chain source the 3TG from conflict-affected and high-risk areas?" }
    };

    const optionsYesNo = [localizer.localizeString("Choose one"), localizer.localizeString("Yes"), localizer.localizeString("No")];
    const optionsYesNoUnknown = [localizer.localizeString("Choose one"), localizer.localizeString("Yes"), localizer.localizeString("No"), localizer.localizeString("Unknown")];
    const optionsPercentage = [localizer.localizeString("Choose one"), localizer.localizeString("100%"), localizer.localizeString(">90%"),
    localizer.localizeString(">75%"), localizer.localizeString(">50%"), localizer.localizeString("<50%"), localizer.localizeString("None")];
    const optionsYesNoUnknownConformance = [localizer.localizeString("Choose one"), localizer.localizeString("YesConformanceWithIpc1755"), localizer.localizeString("YesOtherFormat"), localizer.localizeString("No")];
    const optionsYesSecYesEuYesSecEuNo = [localizer.localizeString("Choose one"), localizer.localizeString("YesWithTheSEC"), localizer.localizeString("YesWithTheEU"),
    localizer.localizeString("YesWithTheSECAndTheEU"), localizer.localizeString("No")];

    const metalsDict: { [key: string]: string } = {
        "Ta": "Tantalum (Ta)",
        "Sn": "Tin (Sn)",
        "Au": "Gold (Au)",
        "W": "Tungsten (W)"
    };

    const companyQuestions = {
        "DrcConflictFreePolicy": "Have you established a responsible minerals sourcing policy?",
        "IsDrcConflictFreePolicyPublic": "Is your responsible minerals sourcing policy publicly available on your website? (Note - If yes, the user shall specify the URL in the comment field.)",
        "SuppliersMustUseCfsSmelters": "Do you require your direct suppliers to source the 3TG from smelters whose due diligence practices have been validated by an independent third party audit program?",
        "DueDiligenceForCfs": "Have you implemented due diligence measures for responsible sourcing?",
        "ConductConflictMineralsSurvey": "Does your company conduct Conflict Minerals survey(s) of your relevant supplier(s)?",
        "VerifySuppliersDueDiligence": "Do you review due diligence information received from your suppliers against your company's expectations?",
        "VerificationIncludesCorrectiveActions": "Does your review process include corrective action management?",
        "SubjectToSecCmDisclosureRule": "Is your company required to file an annual conflict minerals disclosure?"
    };

    const conflictMineralXmlTemplate: string = 'ConflictMineralV6XmlTemplate';
    const cmrtConflictMinerals: string = "ConflictMinerals";
    const companyInfo: string = "CompanyInfo";

    const getImportedFiles = () => fileList?.filter(f => f.type === FileType.InventoryQuestionnaireCmrtFile);
    const isThereUploadedFile = () => Array.isArray(getImportedFiles()) && getImportedFiles()?.length ? true : false;

    // Missing CMRT Details is being passed in as a generic empty object '{}' instead of as null in at least some cases.
    // This is a catch-all to convert an empty object into an unpopulated but correctly structured CmrtDetails model with initialCmrtDetailsValue.
    const [cmrtDetailsValue, setCmrtDetailsValue] = useState((!value || value === '{}') ? initialCmrtDetailsValue : value);
    const [isCmrtCheckboxSelected, setIsCmrtCheckboxSelected] = useState(false);
    const [isCmrtFileUploaded, setIsCmrtFileUploaded] = useState(isThereUploadedFile());
    const [importedFiles, setImportedFiles] = useState(getImportedFiles);
    const [countriesWithCodes, setCountriesWithCodes] = useState<CountryData>();
    const [smelter, setSmelter] = useState<Smelter>(emptySmelter);
    const [product, setProduct] = useState<Product>(emptyProduct);
    const [smelterIdsToDelete, setSmelterIdsToDelete] = useState({});
    const [productIdsToDelete, setProductIdsToDelete] = useState({});
    const [tableItemId, setTableItemId] = useState<string>("");

    useEffect(() => {
        const getCountries = async () => {
            const result: any = await toxnotServiceClient.FetchCountries() as any;
            const countryData: CountryData = result?.data as CountryData;
            setCountriesWithCodes(countryData);
        };

        getCountries();
        setCmrtDetailsValue(value || initialCmrtDetailsValue);
    }, [])

    const getConflictMinerals = () => {
        return JSON.parse(cmrtDetailsValue ? cmrtDetailsValue : emptyCmrtJson)[conflictMineralXmlTemplate]?.[cmrtConflictMinerals];
    }

    // TODO TREX-2861 - These two methods are a big old hack to unbreak the CMRT page in Surveys
    // Due to a stackup of bugs, there are two competing model structures for Smelter and Product, both of which are functionally incorrect.
    // In an effort to reduce changes to this code until a comprehensive plan is made, I'm adding these hacks.
    // See the ticket for details.
    const getSmelters = () => {
        const conflictMinerals = getConflictMinerals();
        let smelter = conflictMinerals.SmelterList?.Smelter ?? [];

        if (!Array.isArray(smelter)) {
            smelter = [smelter];
        }

        return smelter;
    }

    const getProducts = () => {
        const conflictMinerals = getConflictMinerals();
        let product = conflictMinerals.ProductList?.Product ?? [];

        if (!Array.isArray(product)) {
            product = [product];
        }

        return product;
    }

    const getCmrtDetailsFromJson = (cmrtJson: any) => {
        const newCmrtJson = Object.assign({}, cmrtJson);

        if (newCmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals.SmelterList === null) {
            newCmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals.SmelterList = {};
            newCmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals.SmelterList.Smelter = [];
        }
        if (newCmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals.ProductList === null) {
            newCmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals.ProductList = {};
            newCmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals.ProductList.Product = [];
        }
        setCmrtDetailsValue(JSON.stringify(newCmrtJson));
    }

    const onCheckboxChanged = (selected: boolean) => {
        setIsCmrtCheckboxSelected(selected);
    }

    const onFileUpload = (fileDetails: FileTableEntry, file: File, metadataTypeId: number, index?: number) => {
        $('#loading-modal').modal('show');
        let importedFiles: FileTableEntry[] = [];

        const upload = (async () => {
            if (file) {
                const result = await toxnotServiceClient.uploadInventoryFile(fileDetails.filename, fileDetails.metadata.description, 21, true, true, file, metadataTypeId, fileDetails.inventoryId);

                if (result?.data?.fileId) {
                    fileDetails.fileId = result.data.fileId;
                }
            }

            importedFiles.push(fileDetails);
            setIsCmrtFileUploaded(true);
            setImportedFiles(importedFiles);

            const fetchCmrt: any = await toxnotServiceClient.fetchCmrt(file) as any;
            let cmrtJson = emptyCmrtJson;
            if (fetchCmrt === null) {
                alert(localizer.localizeString("Unable to import CMRT data from the attached file. Please enter data manually."));
                $('#loading-modal').modal('hide');
                return fileDetails;
            }
            if (Object.keys(fetchCmrt.cmrtJson).length === 0 || fetchCmrt.cmrtJson === undefined) {
                alert(localizer.localizeString("Unable to import CMRT data from the attached file. Please enter data manually."));
                cmrtJson = JSON.parse(emptyCmrtJson);
            }
            else {
                cmrtJson = (fetchCmrt?.cmrtJson);
            }

            const cmrtJsonStringified = JSON.stringify(cmrtJson);
            onValueChange(cmrtJsonStringified, "", importedFiles, [fileDetails.fileId]);
            getCmrtDetailsFromJson(cmrtJson);

            $('#loading-modal').modal('hide');
            return fileDetails;
        });

        return upload();
    }

    const onFileDelete = (id: string) => {
        const deleteFile = (async () => {
            await toxnotServiceClient.deleteInventoryFile(id, inventoryId ?? "");

            setIsCmrtFileUploaded(false);
            setImportedFiles([]);
        });

        return deleteFile();
    }

    const onMetadataChange = (file: FileTableEntry) => {
        // required method in FileUploader component
    }
    const onInputValueChange = (fieldKey: string, newValue: any) => {
        let parsedCmrtDetails = JSON.parse(cmrtDetailsValue);
        const keys = fieldKey.split("-");
        const input = keys[0];
        let field = "";
        let subField = "";

        if (keys.length === 1) {
            parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals[input] = newValue;
        } else if (keys.length === 2) {
            field = keys[1];
            parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals[input][field] = newValue
        } else if (keys.length === 3) {
            field = keys[1];
            subField = keys[2];
            parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals[input][field][subField] = newValue
        }

        const dataToSave = JSON.stringify(parsedCmrtDetails);
        setCmrtDetailsValue(dataToSave);
        onValueChange(dataToSave);
    };

    const onTableInputValueChange = (fieldName: string, newValue: string, type: string) => {
        if (type === ItemType.Product) {
            const updatedProduct = { ...product };
            updatedProduct[fieldName as keyof typeof updatedProduct] = newValue;
            setProduct(updatedProduct);
        }
        else if (type === ItemType.Smelter) {
            const updatedSmelter = { ...smelter };
            updatedSmelter[fieldName as keyof typeof updatedSmelter] = newValue;
            setSmelter(updatedSmelter);
        }
    };

    const onDropDownValueChange = (fieldKey: string, newValue: any) => {
        const keys = fieldKey.split("-");
        const question = keys[0];
        const field = keys[1];
        let parsedCmrtDetails = JSON.parse(cmrtDetailsValue);
        field ? parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals[question][field] = newValue
            : parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals[question] = newValue;
        const dataToSave = JSON.stringify(parsedCmrtDetails);

        setCmrtDetailsValue(dataToSave);
        onValueChange(dataToSave);
    }

    const getProductListHeaderRow = (): TableColumn<Product>[] => {
        return [
            {
                value: localizer.localizeString("Product Number")
            },
            {
                value: localizer.localizeString("Product Name")
            },
            {
                value: localizer.localizeString("Comments")
            },
        ];
    }

    const getSmelterListHeaderRow = (): TableColumn<Smelter>[] => {
        return [
            {
                value: localizer.localizeString("Smelter ID")
            },
            {
                value: localizer.localizeString("Metal")
            },
            {
                value: localizer.localizeString("Smelter Name")
            },
            {
                value: localizer.localizeString("Smelter Country")
            },
            {
                value: localizer.localizeString("Source")
            }
        ];
    }

    const getProductListRow = (product: Product, index: number): TableRow => {
        return {
            cols: [
                product.ProductNumber ?? localizer.localizeString("Unknown ProductNumber"),
                product.ProductName ?? localizer.localizeString("Unknown ProductName"),
                product.Comments ?? localizer.localizeString("Unknown Comments")
            ]
        } as TableRow;
    }

    const getSmelterListRow = (smelter: Smelter, index: number): TableRow => {
        return {
            cols: [
                smelter.Id ?? localizer.localizeString("Unknown Smelter ID"),
                smelter.Metal ?? localizer.localizeString("Unknown Metal"),
                smelter.ReferenceName ?? localizer.localizeString("Unknown Smelter Name"),
                getCountryNameByCode(smelter.Country ?? ""),
                smelter.IdSource ?? localizer.localizeString("Unknown Source")
            ]
        } as TableRow;
    }

    const getCountryNameByCode = (code: string): string => {
        return localizer.localizeString(countriesWithCodes ? countriesWithCodes[code.toUpperCase()] || "Unknown Country" : "Unknown Country");
    }

    const handleSmelterRowClick = (e: React.MouseEvent, smelter: Smelter): void => {
        setSmelter(smelter);
        setTableItemId(smelter.Id);

        $('#smelter-details-modal').modal('show');
    }

    const renderSmelterDetailsTableHeaderRow = () => {
        return (
            <thead>
                <tr id="headerrow">
                    <th>
                        {localizer.localizeString("Smelter Detail")}
                    </th>
                    <th>
                        {localizer.localizeString("Value")}
                    </th>
                </tr>
            </thead>
        )
    }

    const renderSmelterDetailsTableBody = () => {
        return (
            <tbody>
                {Object.entries(smelter ?? {}).map(entry => {
                    let propValue = entry[1];
                    if (propValue && typeof propValue !== "string") {
                        propValue = "Unknown";
                    }

                    return (
                        <tr key={entry[0]}>
                            <td><b>{localizer.localizeString(entry[0] === "Id" ? `${entry[0]} (*)` : entry[0])}</b></td>
                            <td><TextInput value={entry[0] === "Country" ? getCountryNameByCode(propValue ?? "") : propValue}
                                onValueChange={(newValue) => onTableInputValueChange(entry[0], newValue, ItemType.Smelter)}
                                additionalClass={entry[0] === 'Id' && propValue === '' ? 'highlighted-input' : ''} />
                            </td>
                        </tr>
                    )
                }
                )}
            </tbody>
        )
    }

    const handleProductRowClick = (e: React.MouseEvent, product: Product): void => {
        setProduct(product);
        setTableItemId(product.ProductNumber);

        $('#product-details-modal').modal('show');
    }

    const renderProductDetailsTableHeaderRow = () => {
        return (
            <thead>
                <tr id="headerrow">
                    <th>
                        {localizer.localizeString("Product Detail")}
                    </th>
                    <th>
                        {localizer.localizeString("Value")}
                    </th>
                </tr>
            </thead>
        )
    }

    const renderProductDetailsTableBody = () => {
        return (
            <tbody>
                {Object.entries(product ?? {}).map((entry) => (
                    <tr key={entry[0]}>
                        <td><b>{localizer.localizeString(entry[0] === "ProductNumber" ? `${entry[0]} (*)` : entry[0])}</b></td>
                        <td>
                            <TextInput
                                value={entry[1]}
                                onValueChange={(newValue) => onTableInputValueChange(entry[0], newValue, ItemType.Product)}
                                additionalClass={entry[0] === 'ProductNumber' && entry[1] === '' ? 'highlighted-input' : ''}
                            />
                        </td>
                    </tr>
                ))}
            </tbody>
        );
    }

    const createSmelterButton_click = () => {
        setSmelter(emptySmelter);
        $('#smelter-details-modal').modal('show');
    }

    const createProductButton_click = () => {
        setProduct(emptyProduct);
        $('#product-details-modal').modal('show');
    }

    const onModalCancel = () => {
        setSmelter(emptySmelter);
        setProduct(emptyProduct);
    }

    const onModalSave = (type: string) => {
        const parsedCmrtDetails = JSON.parse(cmrtDetailsValue);

        if (type === ItemType.Product) {
            const index = parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals.ProductList.Product.findIndex((p: { ProductNumber: string; }) => p.ProductNumber === tableItemId);

            if (product.ProductNumber.length == 0) {
                return;
            }

            if (index !== -1) {
                parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals.ProductList.Product[index] = product;
                setProduct(emptyProduct);
            } else if (!tableItemId) {
                parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals.ProductList.Product.unshift(product);
                setProduct(emptyProduct);
            }
        }
        else if (type === ItemType.Smelter) {
            const index = parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals.SmelterList.Smelter.findIndex((s: { Id: string; }) => s.Id === tableItemId);
            const index1 = parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals.SmelterList.Smelter.findIndex((s: { Id: string; }) => s.Id === tableItemId);

            if (smelter.Id.length == 0) {
                return;
            }
            if (index !== -1) {
                parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals.SmelterList.Smelter[index] = smelter;
                setSmelter(emptySmelter);
            } else if (!tableItemId) {
                parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals.SmelterList.Smelter.unshift(smelter);
                setSmelter(emptySmelter);
            }
        }

        setTableItemId("");
        const dataToSave = JSON.stringify(parsedCmrtDetails);
        setCmrtDetailsValue(dataToSave);
        onValueChange(dataToSave);
    }

    const getSmelterId = (model: Smelter) => {
        return model.Id;
    }

    const updateSelectedSmelters = (selectedIds: { [id: string]: string }) => {
        setSmelterIdsToDelete({ ...selectedIds });
    }

    const deleteSmelterButton_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        if (Object.keys(smelterIdsToDelete).length === 0) {
            alert(localizer.localizeString("You must select one or more smelters to delete."));
            return;
        }

        $('#warn-about-deleting-smelters-modal').modal('show');
    }

    const closeDeleteSmeltersWarningModal_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        $('#warn-about-deleting-smelters-modal').modal('hide');
    }

    const deleteSmeltersButtonConfirm_click = () => {
        const parsedCmrtDetails = JSON.parse(cmrtDetailsValue);
        const values: string[] = Object.values(smelterIdsToDelete);
        const filteredSmelters = parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals.SmelterList.Smelter.filter((s: { Id: string; }) => {
            return !values.some((v) => v === s.Id);
        });

        parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals.SmelterList.Smelter = filteredSmelters;
        const dataToSave = JSON.stringify(parsedCmrtDetails);
        setCmrtDetailsValue(dataToSave);
        setSmelterIdsToDelete({});
        onValueChange(dataToSave);

        $('#warn-about-deleting-smelters-modal').modal('hide');
    }

    const getProductNumber = (model: Product) => {
        return model.ProductNumber;
    }

    const updateSelectedProducts = (selectedIds: { [id: string]: string }) => {
        setProductIdsToDelete({ ...selectedIds });
    }

    const deleteProductButton_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        if (Object.keys(productIdsToDelete).length === 0) {
            alert(localizer.localizeString("You must select one or more products to delete."));
            return;
        }

        $('#warn-about-deleting-products-modal').modal('show');
    }

    const closeDeleteProductsWarningModal_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        $('#warn-about-deleting-products-modal').modal('hide');
    }

    const deleteProductsButtonConfirm_click = () => {
        const parsedCmrtDetails = JSON.parse(cmrtDetailsValue);
        const values: string[] = Object.values(productIdsToDelete);
        const filteredProducts = parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals.ProductList.Product.filter((p: { ProductNumber: string; }) => {
            return !values.some((v) => v === p.ProductNumber);
        });

        parsedCmrtDetails.ConflictMineralV6XmlTemplate.ConflictMinerals.ProductList.Product = filteredProducts;
        const dataToSave = JSON.stringify(parsedCmrtDetails);
        setCmrtDetailsValue(dataToSave);
        setProductIdsToDelete({});
        onValueChange(dataToSave);

        $('#warn-about-deleting-products-modal').modal('hide');
    }

    return (
        <div className="m-b-10 page-title-border">
            <CheckBox checkboxLabelText={localizer.localizeString("I want to import CMRT")}
                checked={isCmrtFileUploaded}
                onValueChange={onCheckboxChanged}
                isDisabled={isCmrtFileUploaded} />

            {(isCmrtCheckboxSelected || isCmrtFileUploaded) && < FileUploader
                dataSource={importedFiles}
                fileTypes={[{
                    displayValue: localizer.localizeString("CMRT File"),
                    value: FileMetadataType.PlainDocument
                }]}
                uploadFileAction={onFileUpload}
                metadataChangeAction={onMetadataChange}
                deleteFileAction={onFileDelete}
                addButtonLocalised={localizer.localizeString("Import CMRT")}
                localizer={localizer}
                inventoryId={inventoryId}
                shouldAddButtonDisabled={isCmrtFileUploaded} />}

            <h4><strong>{localizer.localizeString("Company Information")}</strong></h4>
            <label>{localizer.localizeString("Company Name")}<span className="p-relative m-l-5"><span className="text-danger p-absolute required-asterisk">*</span></span></label>
            <TextInput value={getConflictMinerals().CompanyInfo.CompanyName}
                onValueChange={(newValue) => onInputValueChange("CompanyInfo-CompanyName", newValue)}
                idPrefix={idPrefix + "company_name"} />

            <label>{localizer.localizeString("Declaration of Scope or Class")}</label>
            <DropdownMenu
                options={["--", "Company", "Product", "Custom"]}
                onOptionSelected={(newValue) => onDropDownValueChange(`DeclarationScope`, newValue)}
                getFriendlyNameForOptions={(o) => localizer.localizeString(o)}
                value={getConflictMinerals().DeclarationScope}
                getUniqueValueForOption={(o) => o}
                id={idPrefix + "company_declaration_scope"} />

            <label>{localizer.localizeString("Description of Scope")}</label>
            <TextInput
                value={getConflictMinerals().DescriptionOfScope}
                onValueChange={(newValue) => onInputValueChange("DescriptionOfScope", newValue)}
                idPrefix={idPrefix + "company_description_scope"} />

            <label>{localizer.localizeString("Company Unique ID")}</label>
            <TextInput
                value={getConflictMinerals().CompanyInfo.CompanyUniqueId}
                onValueChange={(newValue) => onInputValueChange("CompanyInfo-CompanyUniqueId", newValue)}
                idPrefix={idPrefix + "company_unique_id"} />

            <label>{localizer.localizeString("Company Unique ID Authority")}</label>
            <TextInput value={getConflictMinerals().CompanyInfo.CompanyUniqueIdAuthority}
                onValueChange={(newValue) => onInputValueChange("CompanyInfo-CompanyUniqueIdAuthority", newValue)}
                idPrefix={idPrefix + "company_unique_id_authority"} />

            <label>{localizer.localizeString("Address")}</label>
            <TextInput value={getConflictMinerals().CompanyInfo.CompanyAddress}
                onValueChange={(newValue) => onInputValueChange("CompanyInfo-CompanyAddress", newValue)}
                idPrefix={idPrefix + "company_address"} />

            <label>{localizer.localizeString("Contact Name")}</label>
            <TextInput value={getConflictMinerals().CompanyInfo.ContactName}
                onValueChange={(newValue) => onInputValueChange("CompanyInfo-ContactName", newValue)}
                idPrefix={idPrefix + "company_contact_name"} />

            <label>{localizer.localizeString("Email - Contact")}<span className="p-relative m-l-5"><span className="text-danger p-absolute required-asterisk">*</span></span></label>
            <TextInput value={getConflictMinerals().CompanyInfo.ContactEmail}
                onValueChange={(newValue) => onInputValueChange("CompanyInfo-ContactEmail", newValue)}
                idPrefix={idPrefix + "company_email_contact"} />

            <label>{localizer.localizeString("Phone - Contact")}</label>
            <div className="row">
                <div className="col-sm-4">
                    <label>{localizer.localizeString("Country")}</label>
                    <TextInput value={getConflictMinerals().CompanyInfo.ContactPhone?.CountryCode}
                        onValueChange={(newValue) => onInputValueChange("CompanyInfo-ContactPhone-CountryCode", newValue)}
                        idPrefix={idPrefix + "company_phone_contact_country"} />
                </div>
                <div className="col-sm-4">
                    <label>{localizer.localizeString("Number")}</label>
                    <TextInput value={getConflictMinerals().CompanyInfo.ContactPhone?.Number}
                        onValueChange={(newValue) => onInputValueChange("CompanyInfo-ContactPhone-Number", newValue)}
                        idPrefix={idPrefix + "company_phone_contact_number"} />

                </div>
                <div className="col-sm-4">
                    <label>{localizer.localizeString("Ext")}</label>
                    <TextInput value={getConflictMinerals().CompanyInfo.ContactPhone?.AreaCode}
                        onValueChange={(newValue) => onInputValueChange("CompanyInfo-ContactPhone-AreaCode", newValue)}
                        idPrefix={idPrefix + "company_phone_contact_ext"} />
                </div>
            </div>

            <label>{localizer.localizeString("Authorizer")}</label>
            <TextInput value={getConflictMinerals().CompanyInfo.AuthorizerName}
                onValueChange={(newValue) => onInputValueChange("CompanyInfo-AuthorizerName", newValue)}
                idPrefix={idPrefix + "company_authorizer_name"} />

            <label>{localizer.localizeString("Title")}</label>
            <TextInput value={getConflictMinerals().CompanyInfo.AuthorizerTitle}
                onValueChange={(newValue) => onInputValueChange("CompanyInfo-AuthorizerTitle", newValue)}
                idPrefix={idPrefix + "company_authorizer_title"} />

            <label>{localizer.localizeString("Email")}</label>
            <TextInput value={getConflictMinerals().CompanyInfo.AuthorizerEmail}
                onValueChange={(newValue) => onInputValueChange("CompanyInfo-AuthorizerEmail", newValue)}
                idPrefix={idPrefix + "company_authorizer_email"} />

            <label>{localizer.localizeString("Phone")}</label>
            <div className="row">
                <div className="col-sm-4">
                    <label>{localizer.localizeString("Country")}</label>
                    <TextInput value={getConflictMinerals().CompanyInfo.AuthorizerPhone?.CountryCode}
                        onValueChange={(newValue) => onInputValueChange("CompanyInfo-AuthorizerPhone-CountryCode", newValue)}
                        idPrefix={idPrefix + "company_phone_authorizer_country"} />
                </div>
                <div className="col-sm-4">
                    <label>{localizer.localizeString("Number")}</label>
                    <TextInput value={getConflictMinerals().CompanyInfo.AuthorizerPhone?.Number}
                        onValueChange={(newValue) => onInputValueChange("CompanyInfo-AuthorizerPhone-Number", newValue)}
                        idPrefix={idPrefix + "company_phone_authorizer_number"} />

                </div>
                <div className="col-sm-4">
                    <label>{localizer.localizeString("Ext")}</label>
                    <TextInput value={getConflictMinerals().CompanyInfo.AuthorizerPhone?.AreaCode}
                        onValueChange={(newValue) => onInputValueChange("CompanyInfo-AuthorizerPhone-AreaCode", newValue)}
                        idPrefix={idPrefix + "company_phone_authorizer_ext"} />
                </div>
            </div>

            <label>{localizer.localizeString("Effective Date")}</label>
            <div className="question-response m-l-15">
                <DateInput value={getConflictMinerals().CompanyInfo.EffectiveDate}
                    onValueChange={(newValue) => onInputValueChange("CompanyInfo-EffectiveDate", newValue)}
                />
            </div>

            <ToxnotReactModal modalId={"loading-modal"} localizedTitle="File upload">
                <ToxnotReactModalBody>
                    <div className="alert alert-warning">
                        <p>
                            {localizer.localizeString("Uploading file ...")}
                        </p>
                        <p>
                            {localizer.localizeString("Please wait to finish file upload.")}
                        </p>
                    </div>
                </ToxnotReactModalBody>
            </ToxnotReactModal>

            <h4><strong>{localizer.localizeString("Compliance Questions")}</strong></h4>
            <div className="question" key={Question.IntentionallyAddedOrUsed}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`1. ${localizer.localizeString(complianceQuestionnaire[Question.IntentionallyAddedOrUsed].question)}`)}</strong>
                        <span className="p-relative m-l-5"><span className="text-danger p-absolute required-asterisk">*</span></span>
                    </p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(Metal).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(metalsDict[key])}</label>
                                <DropdownMenu options={optionsYesNo}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`${Question.IntentionallyAddedOrUsed}-${key}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals()[Question.IntentionallyAddedOrUsed][key as MetalKey]}
                                    getUniqueValueForOption={o => o}
                                    id={complianceQuestionnaire[Question.IntentionallyAddedOrUsed].id + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals()[Question.IntentionallyAddedOrUsed][key + "Comment" as MetalKey]}
                                    onValueChange={(newValue) => onInputValueChange(`${Question.IntentionallyAddedOrUsed}-${key}Comment`, newValue)}
                                    idPrefix={"comment" + complianceQuestionnaire[Question.IntentionallyAddedOrUsed].id + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={Question.RemainedInProduct}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`2. ${localizer.localizeString(complianceQuestionnaire[Question.RemainedInProduct].question)}`)}</strong>
                        <span className="p-relative m-l-5"><span className="text-danger p-absolute required-asterisk">*</span></span>
                    </p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(Metal).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(metalsDict[key])}</label>
                                <DropdownMenu options={optionsYesNo}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`${Question.RemainedInProduct}-${key}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals()[Question.RemainedInProduct][key as MetalKey]}
                                    getUniqueValueForOption={o => o}
                                    id={complianceQuestionnaire[Question.RemainedInProduct].id + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals()[Question.RemainedInProduct][key + "Comment" as MetalKey]}
                                    onValueChange={(newValue) => onInputValueChange(`${Question.RemainedInProduct}-${key}Comment`, newValue)}
                                    idPrefix={"comment" + complianceQuestionnaire[Question.RemainedInProduct].id + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={Question.DrcOrigin}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`3. ${localizer.localizeString(complianceQuestionnaire[Question.DrcOrigin].question)}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(Metal).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(metalsDict[key])}</label>
                                <DropdownMenu options={optionsYesNoUnknown}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`${Question.DrcOrigin}-${key}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals()[Question.DrcOrigin][key as MetalKey]}
                                    getUniqueValueForOption={o => o}
                                    id={complianceQuestionnaire[Question.DrcOrigin].id + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals()[Question.DrcOrigin][key + "Comment" as MetalKey]}
                                    onValueChange={(newValue) => onInputValueChange(`${Question.DrcOrigin}-${key}Comment`, newValue)}
                                    idPrefix={"comment" + complianceQuestionnaire[Question.DrcOrigin].id + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={Question.ScrapOrigin}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`4. ${localizer.localizeString(complianceQuestionnaire[Question.ScrapOrigin].question)}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(Metal).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(metalsDict[key])}</label>
                                <DropdownMenu options={optionsYesNoUnknown}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`${Question.ScrapOrigin}-${key}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals()[Question.ScrapOrigin][key as MetalKey]}
                                    getUniqueValueForOption={o => o}
                                    id={complianceQuestionnaire[Question.ScrapOrigin].id + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals()[Question.ScrapOrigin][key + "Comment" as MetalKey]}
                                    onValueChange={(newValue) => onInputValueChange(`${Question.ScrapOrigin}-${key}Comment`, newValue)}
                                    idPrefix={"comment" + complianceQuestionnaire[Question.ScrapOrigin].id + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={Question.PercentageSuppliersProvidedResponse}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`5. ${localizer.localizeString(complianceQuestionnaire[Question.PercentageSuppliersProvidedResponse].question)}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(Metal).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(metalsDict[key])}</label>
                                <DropdownMenu options={optionsPercentage}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`${Question.PercentageSuppliersProvidedResponse}-${key}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals()[Question.PercentageSuppliersProvidedResponse][key as MetalKey]}
                                    getUniqueValueForOption={o => o}
                                    id={complianceQuestionnaire[Question.PercentageSuppliersProvidedResponse].id + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals()[Question.PercentageSuppliersProvidedResponse][key + "Comment" as MetalKey]}
                                    onValueChange={(newValue) => onInputValueChange(`${Question.PercentageSuppliersProvidedResponse}-${key}Comment`, newValue)}
                                    idPrefix={"comment" + complianceQuestionnaire[Question.PercentageSuppliersProvidedResponse].id + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={Question.KnownSmelters}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`6. ${localizer.localizeString(complianceQuestionnaire[Question.KnownSmelters].question)}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(Metal).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(metalsDict[key])}</label>
                                <DropdownMenu options={optionsYesNo}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`${Question.KnownSmelters}-${key}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals()[Question.KnownSmelters][key as MetalKey]}
                                    getUniqueValueForOption={o => o}
                                    id={complianceQuestionnaire[Question.KnownSmelters].id + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals()[Question.KnownSmelters][key + "Comment" as MetalKey]}
                                    onValueChange={(newValue) => onInputValueChange(`${Question.KnownSmelters}-${key}Comment`, newValue)}
                                    idPrefix={"comment" + complianceQuestionnaire[Question.KnownSmelters].id + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={Question.CfsValid}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`7. ${localizer.localizeString(complianceQuestionnaire[Question.CfsValid].question)}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(Metal).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(metalsDict[key])}</label>
                                <DropdownMenu options={optionsYesNoUnknown}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`${Question.CfsValid}-${key}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals()[Question.CfsValid][key as MetalKey]}
                                    getUniqueValueForOption={o => o}
                                    id={complianceQuestionnaire[Question.CfsValid].id + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals()[Question.CfsValid][key + "Comment" as MetalKey]}
                                    onValueChange={(newValue) => onInputValueChange(`${Question.CfsValid}-${key}Comment`, newValue)}
                                    idPrefix={"comment" + complianceQuestionnaire[Question.CfsValid].id + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={Question.Smelter3TGConflictAndHighRiskArea}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`8. ${localizer.localizeString(complianceQuestionnaire[Question.Smelter3TGConflictAndHighRiskArea].question)}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(Metal).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(metalsDict[key])}</label>
                                <DropdownMenu options={optionsYesNo}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`${Question.Smelter3TGConflictAndHighRiskArea}-${key}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals()[Question.Smelter3TGConflictAndHighRiskArea][key as MetalKey]}
                                    getUniqueValueForOption={o => o}
                                    id={complianceQuestionnaire[Question.Smelter3TGConflictAndHighRiskArea].id + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals()[Question.Smelter3TGConflictAndHighRiskArea][key + "Comment" as MetalKey]}
                                    onValueChange={(newValue) => onInputValueChange(`${Question.Smelter3TGConflictAndHighRiskArea}-${key}Comment`, newValue)}
                                    idPrefix={"comment" + complianceQuestionnaire[Question.Smelter3TGConflictAndHighRiskArea].id + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <h4><strong>{localizer.localizeString("Company Level Questions")}</strong></h4>
            <div className="question" key={CompanyQuestion.DrcConflictFreePolicy}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`A. ${companyQuestions[CompanyQuestion.DrcConflictFreePolicy]}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(CompanyAnswer).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(CompanyAnswer.Answer)}</label>
                                <DropdownMenu options={optionsYesNo}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`CompanyWideQuestions-${CompanyQuestion.DrcConflictFreePolicy}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.DrcConflictFreePolicy]}
                                    getUniqueValueForOption={o => o}
                                    id={CompanyQuestion.DrcConflictFreePolicy + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.DrcConflictFreePolicy + "Comment" as CompanyAnswerKey]}
                                    onValueChange={(newValue) => onInputValueChange(`CompanyWideQuestions-${CompanyQuestion.DrcConflictFreePolicy}Comment`, newValue)}
                                    idPrefix={"comment" + CompanyQuestion.DrcConflictFreePolicy + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={CompanyQuestion.IsDrcConflictFreePolicyPublic}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`B. ${companyQuestions[CompanyQuestion.IsDrcConflictFreePolicyPublic]}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(CompanyAnswer).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(CompanyAnswer.Answer)}</label>
                                <DropdownMenu options={optionsYesNo}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`CompanyWideQuestions-${CompanyQuestion.IsDrcConflictFreePolicyPublic}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.IsDrcConflictFreePolicyPublic]}
                                    getUniqueValueForOption={o => o}
                                    id={CompanyQuestion.IsDrcConflictFreePolicyPublic + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.IsDrcConflictFreePolicyPublic + "Comment" as CompanyAnswerKey]}
                                    onValueChange={(newValue) => onInputValueChange(`CompanyWideQuestions-${CompanyQuestion.IsDrcConflictFreePolicyPublic}Comment`, newValue)}
                                    idPrefix={"comment" + CompanyQuestion.IsDrcConflictFreePolicyPublic + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={CompanyQuestion.SuppliersMustUseCfsSmelters}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`C. ${companyQuestions[CompanyQuestion.SuppliersMustUseCfsSmelters]}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(CompanyAnswer).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(CompanyAnswer.Answer)}</label>
                                <DropdownMenu options={optionsYesNo}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`CompanyWideQuestions-${CompanyQuestion.SuppliersMustUseCfsSmelters}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.SuppliersMustUseCfsSmelters]}
                                    getUniqueValueForOption={o => o}
                                    id={CompanyQuestion.SuppliersMustUseCfsSmelters + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.SuppliersMustUseCfsSmelters + "Comment" as CompanyAnswerKey]}
                                    onValueChange={(newValue) => onInputValueChange(`CompanyWideQuestions-${CompanyQuestion.SuppliersMustUseCfsSmelters}Comment`, newValue)}
                                    idPrefix={"comment" + CompanyQuestion.SuppliersMustUseCfsSmelters + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={CompanyQuestion.DueDiligenceForCfs}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`D. ${companyQuestions[CompanyQuestion.DueDiligenceForCfs]}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(CompanyAnswer).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(CompanyAnswer.Answer)}</label>
                                <DropdownMenu options={optionsYesNo}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`CompanyWideQuestions-${CompanyQuestion.DueDiligenceForCfs}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.DueDiligenceForCfs]}
                                    getUniqueValueForOption={o => o}
                                    id={CompanyQuestion.DueDiligenceForCfs + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.DueDiligenceForCfs + "Comment" as CompanyAnswerKey]}
                                    onValueChange={(newValue) => onInputValueChange(`CompanyWideQuestions-${CompanyQuestion.DueDiligenceForCfs}Comment`, newValue)}
                                    idPrefix={"comment" + CompanyQuestion.DueDiligenceForCfs + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={CompanyQuestion.ConductConflictMineralsSurvey}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`E. ${companyQuestions[CompanyQuestion.ConductConflictMineralsSurvey]}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(CompanyAnswer).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(CompanyAnswer.Answer)}</label>
                                <DropdownMenu options={optionsYesNoUnknownConformance}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`CompanyWideQuestions-${CompanyQuestion.ConductConflictMineralsSurvey}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.ConductConflictMineralsSurvey]}
                                    getUniqueValueForOption={o => o}
                                    id={CompanyQuestion.ConductConflictMineralsSurvey + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.ConductConflictMineralsSurvey + "Comment" as CompanyAnswerKey]}
                                    onValueChange={(newValue) => onInputValueChange(`CompanyWideQuestions-${CompanyQuestion.ConductConflictMineralsSurvey}Comment`, newValue)}
                                    idPrefix={"comment" + CompanyQuestion.ConductConflictMineralsSurvey + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={CompanyQuestion.VerifySuppliersDueDiligence}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`F. ${companyQuestions[CompanyQuestion.VerifySuppliersDueDiligence]}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(CompanyAnswer).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(CompanyAnswer.Answer)}</label>
                                <DropdownMenu options={optionsYesNo}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`CompanyWideQuestions-${CompanyQuestion.VerifySuppliersDueDiligence}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.VerifySuppliersDueDiligence]}
                                    getUniqueValueForOption={o => o}
                                    id={CompanyQuestion.VerifySuppliersDueDiligence + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.VerifySuppliersDueDiligence + "Comment" as CompanyAnswerKey]}
                                    onValueChange={(newValue) => onInputValueChange(`CompanyWideQuestions-${CompanyQuestion.VerifySuppliersDueDiligence}Comment`, newValue)}
                                    idPrefix={"comment" + CompanyQuestion.VerifySuppliersDueDiligence + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={CompanyQuestion.VerificationIncludesCorrectiveActions}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`G. ${companyQuestions[CompanyQuestion.VerificationIncludesCorrectiveActions]}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(CompanyAnswer).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(CompanyAnswer.Answer)}</label>
                                <DropdownMenu options={optionsYesNo}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`CompanyWideQuestions-${CompanyQuestion.VerificationIncludesCorrectiveActions}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.VerificationIncludesCorrectiveActions]}
                                    getUniqueValueForOption={o => o}
                                    id={CompanyQuestion.VerificationIncludesCorrectiveActions + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.VerificationIncludesCorrectiveActions + "Comment" as CompanyAnswerKey]}
                                    onValueChange={(newValue) => onInputValueChange(`CompanyWideQuestions-${CompanyQuestion.VerificationIncludesCorrectiveActions}Comment`, newValue)}
                                    idPrefix={"comment" + CompanyQuestion.VerificationIncludesCorrectiveActions + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>

            <div className="question" key={CompanyQuestion.SubjectToSecCmDisclosureRule}>
                <div className="question-text">
                    <p><strong>{localizer.localizeString(`H. ${companyQuestions[CompanyQuestion.SubjectToSecCmDisclosureRule]}`)}</strong></p>
                </div>
                <div className="question-response m-l-15">
                    {Object.keys(CompanyAnswer).map(key => {
                        return <div key={key} className="row">
                            <div key={key} className="form-group col-xs-3">
                                <label>{localizer.localizeString(CompanyAnswer.Answer)}</label>
                                <DropdownMenu options={optionsYesSecYesEuYesSecEuNo}
                                    onOptionSelected={(newValue) => onDropDownValueChange(`CompanyWideQuestions-${CompanyQuestion.SubjectToSecCmDisclosureRule}`, newValue)}
                                    getFriendlyNameForOptions={o => o}
                                    value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.SubjectToSecCmDisclosureRule]}
                                    getUniqueValueForOption={o => o}
                                    id={CompanyQuestion.SubjectToSecCmDisclosureRule + "-" + key} />
                            </div>
                            <div key={key + "_comment"} className="form-group col-xs-9">
                                <label>{localizer.localizeString("Comment")}</label>
                                <TextInput value={getConflictMinerals().CompanyWideQuestions[CompanyQuestion.SubjectToSecCmDisclosureRule + "Comment" as CompanyAnswerKey]}
                                    onValueChange={(newValue) => onInputValueChange(`CompanyWideQuestions-${CompanyQuestion.SubjectToSecCmDisclosureRule}Comment`, newValue)}
                                    idPrefix={"comment" + CompanyQuestion.SubjectToSecCmDisclosureRule + "-" + key} />
                            </div>
                        </div>
                    })}
                </div>
            </div>




            <h4><strong>{localizer.localizeString("Smelter List")}</strong></h4>
            <TopActionButtons>
                <div>
                    <button className="btn btn-default" onClick={e => createSmelterButton_click()}><span className="glyphicon glyphicon-plus" /> {localizer.localizeString("Create Smelter")}</button>
                    <button className="btn btn-default" onClick={e => deleteSmelterButton_click(e)}><span className="glyphicon glyphicon-remove" /> {localizer.localizeString("Delete Smelter")}</button>
                </div>
            </TopActionButtons>
            <div id="smelter-list-table" className="tn-section-padding-container">
                <ToxnotReactTable
                    allRowModels={getSmelters()}
                    localizer={localizer}
                    headerRow={getSmelterListHeaderRow()}
                    getRowForTable={getSmelterListRow}
                    rowClickEventHandler={handleSmelterRowClick}
                    selectingRowOptions={{
                        rowModelGetId: getSmelterId,
                        onRowSelected: updateSelectedSmelters
                    }}
                    enablePaging={true}
                    fullSectionWidth={true} />
            </div>

            <ToxnotReactModal modalId={"smelter-details-modal"} localizedTitle={localizer.localizeString("Smelter Details")}>
                <ToxnotReactModalBody>
                    <table id="createTableSmelter" className="table table-bordered">
                        {renderSmelterDetailsTableHeaderRow()}
                        {renderSmelterDetailsTableBody()}
                    </table>
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer}
                    onCancel={onModalCancel}>
                    {<button type="button" className="btn btn-toxnot-primary"
                        data-dismiss="modal"
                        onClick={() => onModalSave("Smelter")}
                        disabled={smelter.Id.length == 0 ? true : false}>
                        {localizer.localizeString("Save")}
                    </button>}
                </ToxnotReactModalFooter>
            </ToxnotReactModal>

            <ToxnotReactModal modalId={"warn-about-deleting-smelters-modal"} localizedTitle={localizer.localizeString("Are you sure you want to delete?")}>
                <ToxnotReactModalBody>
                    <div className="alert alert-warning">
                        <p>
                            {localizer.localizeString("You are about to delete smelter(s). This will remove them from the Exchange.")}
                        </p>
                        <p>
                            {localizer.localizeString("Are you sure you want to continue?")}
                        </p>
                    </div>
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer} enabledCloseButton={false}>
                    <button className="btn btn-default" onClick={closeDeleteSmeltersWarningModal_click}>{localizer.localizeString("Cancel")}</button>
                    <button className="btn btn-danger" onClick={deleteSmeltersButtonConfirm_click}>{localizer.localizeString("Yes, Delete")}</button>
                </ToxnotReactModalFooter>
            </ToxnotReactModal>



            <h4><strong>{localizer.localizeString("Product List")}</strong></h4>
            <TopActionButtons>
                <div>
                    <button className="btn btn-default" onClick={e => createProductButton_click()}><span className="glyphicon glyphicon-plus" /> {localizer.localizeString("Create Product")}</button>
                    <button className="btn btn-default" onClick={e => deleteProductButton_click(e)}><span className="glyphicon glyphicon-remove" /> {localizer.localizeString("Delete Product")}</button>
                </div>
            </TopActionButtons>
            <div id="product-list-table" className="tn-section-padding-container">
                <ToxnotReactTable
                    allRowModels={getProducts()}
                    localizer={localizer}
                    headerRow={getProductListHeaderRow()}
                    getRowForTable={getProductListRow}
                    rowClickEventHandler={handleProductRowClick}
                    selectingRowOptions={{
                        rowModelGetId: getProductNumber,
                        onRowSelected: updateSelectedProducts
                    }}
                    enablePaging={true}
                    fullSectionWidth={true} />
            </div>

            <ToxnotReactModal modalId={"product-details-modal"} localizedTitle={localizer.localizeString("Product Details")}>
                <ToxnotReactModalBody>
                    <table id="createTableProduct" className="table table-bordered">
                        {renderProductDetailsTableHeaderRow()}
                        {renderProductDetailsTableBody()}
                    </table>
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer}
                    onCancel={onModalCancel}>
                    {<button
                        type="button" className="btn btn-toxnot-primary" data-dismiss="modal"
                        onClick={() => onModalSave("Product")}
                        disabled={product.ProductNumber.length == 0 ? true : false}>
                        {localizer.localizeString("Save")}
                    </button>}
                </ToxnotReactModalFooter>
            </ToxnotReactModal>

            <ToxnotReactModal modalId={"warn-about-deleting-products-modal"} localizedTitle={localizer.localizeString("Are you sure you want to delete?")}>
                <ToxnotReactModalBody>
                    <div className="alert alert-warning">
                        <p>
                            {localizer.localizeString("You are about to delete products(s). This will remove them from the Exchange.")}
                        </p>
                        <p>
                            {localizer.localizeString("Are you sure you want to continue?")}
                        </p>
                    </div>
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer} enabledCloseButton={false}>
                    <button className="btn btn-default" onClick={closeDeleteProductsWarningModal_click}>{localizer.localizeString("Cancel")}</button>
                    <button className="btn btn-danger" onClick={deleteProductsButtonConfirm_click}>{localizer.localizeString("Yes, Delete")}</button>
                </ToxnotReactModalFooter>
            </ToxnotReactModal>


        </div>
    );
};

export default CmrtDetails;
